import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { UserService } from 'app/core/user/user.service';
import { ADMIN_ROLE, User } from 'app/core/user/user.types';
import { SwitchSocietyResponse } from 'app/modules/platform/platform.types';
import { BehaviorSubject, Observable } from 'rxjs';

export interface ImpersonationState {
    active: boolean;
    societyName?: string;
}

const TOKEN_BACKUP_KEY = 'superAdminToken';
const USER_BACKUP_KEY = 'superAdminUserData';
const SOCIETY_NAME_KEY = 'impersonatingSocietyName';

/**
 * Lets a SuperAdmin "switch into" a society: it swaps the active access token
 * for the society-scoped one returned by the API and overlays an Admin-style
 * user, so the existing society admin screens work unchanged. The original
 * SuperAdmin token/user are stashed and restored on exit.
 */
@Injectable({ providedIn: 'root' })
export class ImpersonationService {
    private _authService = inject(AuthService);
    private _userService = inject(UserService);
    private _router = inject(Router);

    private _state = new BehaviorSubject<ImpersonationState>(
        this._readStateFromStorage()
    );

    get state$(): Observable<ImpersonationState> {
        return this._state.asObservable();
    }

    get isImpersonating(): boolean {
        return !!localStorage.getItem(TOKEN_BACKUP_KEY);
    }

    /** Enter a society using the scoped token from POST /platform/switch-society. */
    enter(scoped: SwitchSocietyResponse): void {
        // Back up the SuperAdmin's current token + user (only on first entry,
        // so re-switching between societies doesn't clobber the backup).
        if (!this.isImpersonating) {
            localStorage.setItem(TOKEN_BACKUP_KEY, this._authService.accessToken);
            const currentUser = localStorage.getItem('userData');
            if (currentUser) {
                localStorage.setItem(USER_BACKUP_KEY, currentUser);
            }
        }

        const superUser = this._parseUser(localStorage.getItem(USER_BACKUP_KEY));

        const impersonatedUser: User = {
            id: superUser?.id ?? 0,
            name: superUser?.name ?? 'Super Admin',
            email: superUser?.email ?? '',
            societyId: scoped.societyId,
            societyName: scoped.societyName,
            roleName: scoped.roleName || ADMIN_ROLE,
        };

        this._authService.accessToken = scoped.token;
        localStorage.setItem('userData', JSON.stringify(impersonatedUser));
        localStorage.setItem(SOCIETY_NAME_KEY, scoped.societyName ?? '');
        this._userService.user = impersonatedUser;

        this._state.next({ active: true, societyName: scoped.societyName });

        this._router.navigateByUrl('/dashboard');
    }

    /** Exit the society and restore the SuperAdmin session. */
    exit(): void {
        const token = localStorage.getItem(TOKEN_BACKUP_KEY);
        const userRaw = localStorage.getItem(USER_BACKUP_KEY);

        if (token) {
            this._authService.accessToken = token;
        }
        if (userRaw) {
            localStorage.setItem('userData', userRaw);
            const user = this._parseUser(userRaw);
            if (user) {
                this._userService.user = user;
            }
        }

        localStorage.removeItem(TOKEN_BACKUP_KEY);
        localStorage.removeItem(USER_BACKUP_KEY);
        localStorage.removeItem(SOCIETY_NAME_KEY);

        this._state.next({ active: false });

        this._router.navigateByUrl('/platform/societies');
    }

    private _readStateFromStorage(): ImpersonationState {
        if (localStorage.getItem(TOKEN_BACKUP_KEY)) {
            return {
                active: true,
                societyName: localStorage.getItem(SOCIETY_NAME_KEY) ?? undefined,
            };
        }
        return { active: false };
    }

    private _parseUser(raw: string | null): User | null {
        if (!raw) {
            return null;
        }
        try {
            return JSON.parse(raw) as User;
        } catch {
            return null;
        }
    }
}
